import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Receipt, Download, ShieldCheck, ArrowRight, Printer } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import StatusPill from "@/components/marketplace/StatusPill";

export default function ReceiptsPage() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  useDocumentTitle("Receipts & payment history");

  useEffect(() => {
    api.get("/payments/mine").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  }, []);

  const openReceipt = async (p) => {
    setSelected(p);
  };

  return (
    <DashboardShell>
      <div className="max-w-6xl">
        <div className="mb-6">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Receipts</div>
          <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Your payment history</h1>
          <p className="text-[#252A34]/70 text-sm mt-1">Every Stripe test-mode transaction lands here with a full fee breakdown.</p>
        </div>
        {loading ? (
          <div className="text-sm text-[#252A34]/60">Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <Receipt className="h-6 w-6 mx-auto text-[#165a71]" />
            <div className="mt-2 font-display text-lg text-[#071b22] font-semibold">No receipts yet.</div>
            <div className="text-sm text-[#252A34]/70 mt-1">Your first Stripe test-mode payment will appear here.</div>
            <Link to="/services" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-5 py-2.5 text-sm font-medium">Browse services <ArrowRight className="h-4 w-4" /></Link>
          </div>
        ) : (
          <div className="grid gap-3">
            {items.map((p) => (
              <button key={p.id} onClick={() => openReceipt(p)} className="text-left rounded-2xl border border-slate-200 bg-white p-4 md:p-5 flex items-center gap-4 hover:border-[#165a71]/40" data-testid={`receipt-${p.id}`}>
                <div className="h-12 w-12 rounded-xl bg-[#165a71]/10 flex items-center justify-center text-[#165a71]"><Receipt className="h-5 w-5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-[#071b22] capitalize">{p.purpose.replace(/_/g, " ")}</div>
                  <div className="text-xs text-[#252A34]/60 mt-1">{p.created_at ? new Date(p.created_at).toLocaleDateString() : ""} · <span className="font-mono text-[10px]">{p.stripe_session_id || p.id}</span></div>
                </div>
                <div className="text-right">
                  <div className="font-display text-lg font-semibold text-[#071b22]">${(p.gross_amount || 0).toFixed(2)}</div>
                  <StatusPill status={p.status} />
                </div>
              </button>
            ))}
          </div>
        )}

        {selected && <ReceiptModal payment={selected} onClose={() => setSelected(null)} />}
      </div>
    </DashboardShell>
  );
}

function ReceiptModal({ payment, onClose }) {
  const s = payment.fee_snapshot || {};
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 print:hidden" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="receipt-modal">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="font-display text-xl font-semibold text-[#071b22]">Receipt</div>
          <div className="flex items-center gap-2">
            <button onClick={() => window.print()} className="rounded-lg border border-slate-200 p-2 text-[#252A34]" data-testid="print-receipt"><Printer className="h-4 w-4" /></button>
            <button onClick={onClose} className="text-sm">✕</button>
          </div>
        </div>
        <div className="p-6 space-y-4 print:p-8">
          <div className="text-center">
            <div className="font-display text-2xl font-semibold text-[#071b22]">Gulf Coast Work</div>
            <div className="text-xs text-[#252A34]/60 uppercase tracking-[0.2em]">Test-mode receipt</div>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm">
            <Row label="Purpose" value={payment.purpose?.replace(/_/g, " ")} />
            <Row label="Payment ID" value={<span className="font-mono text-[11px]">{payment.id}</span>} />
            <Row label="Stripe session" value={<span className="font-mono text-[11px]">{payment.stripe_session_id || "—"}</span>} />
            <Row label="Date" value={payment.created_at ? new Date(payment.created_at).toLocaleString() : ""} />
            <Row label="Status" value={<span className="capitalize">{payment.status}</span>} />
          </div>
          <div className="rounded-xl border border-slate-200 p-4 text-sm space-y-1.5">
            <Row label="Subtotal" value={`$${(s.subtotal || 0).toFixed(2)}`} />
            {s.customer_service_fee_amount > 0 && <Row label={`Service fee (${s.customer_service_fee_pct}%)`} value={`+$${s.customer_service_fee_amount.toFixed(2)}`} />}
            {s.tip_amount > 0 && <Row label="Tip" value={`+$${s.tip_amount.toFixed(2)}`} />}
            <div className="border-t border-slate-200 pt-2 mt-2 flex items-center justify-between font-display font-semibold text-[#071b22]">
              <span>Total charged</span>
              <span>${(payment.gross_amount || 0).toFixed(2)}</span>
            </div>
          </div>
          <div className="text-[11px] text-[#252A34]/60">
            Provider net: ${(s.provider_net || 0).toFixed(2)} · Platform fee: ${(s.platform_fee || 0).toFixed(2)} · Commission: {s.commission_pct || 15}%.
            {payment.refunded_amount > 0 && <div className="mt-1 text-red-600">Refunded: ${payment.refunded_amount.toFixed(2)}</div>}
          </div>
          <div className="text-[10px] text-[#252A34]/50 inline-flex items-center gap-1.5">
            <ShieldCheck className="h-3 w-3" /> Powered by Stripe · Test mode. No real charge occurred.
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-baseline justify-between text-[#252A34] gap-4">
    <span className="text-[#252A34]/60 uppercase text-[10px] tracking-wider">{label}</span>
    <span className="text-[#071b22] text-right">{value}</span>
  </div>
);
